import { CSSObject } from '@emotion/react';
import { Venue } from '@graphql/gql/graphql';
import { useTranslate } from 'nucleus-text';
import useTheme from '@hooks/useTheme';
import getConfig from 'next/config';
import Button from '@cvent/carina/components/Button';
import useBreakpoints from '@hooks/useBreakpoints';

const { publicRuntimeConfig } = getConfig();

const GOOGLE_MAP_MAX_WIDTH = '696';

interface VenueContentProps {
  venueInfo: Venue;
}
const useStyles = (): Record<string, CSSObject> => {
  const breakpoints = useBreakpoints();
  const { mediaIsM, mediaIsLargeAndAbove } = breakpoints;

  const { font } = useTheme();
  return {
    map: {
      height: '13.1rem',
      [mediaIsLargeAndAbove]: {
        height: '20.5rem'
      },
      [mediaIsM]: { height: '16rem' }
    },
    venueText: {
      fontFamily: font.base.family,
      fontSize: font.base.size[7],
      fontWeight: font.base.weight.bold,
      lineHeight: font.lineHeight.prose,
      marginBottom: '1rem',
      marginTop: '0rem'
    },
    venueNameText: {
      fontFamily: font.base.family,
      fontSize: font.base.size.l,
      fontWeight: font.base.weight.bold,
      lineHeight: font.lineHeight.base
    },
    addressText: {
      fontFamily: font.base.family,
      fontSize: font.base.size.l,
      fontWeight: font.base.weight.regular,
      lineHeight: font.lineHeight.base,
      paddingTop: '0.5rem',
      paddingBottom: '1rem'
    },
    getDirectionButton: { paddingTop: '1.5rem' }
  };
};

const getGoogleMapUri = (encodedMarkersAddress: string, googleApiKey: string): string => {
  const zoom = 15; // Zoom level
  const height = 328; // MaxHeight of the map image
  const scale = 1; // Scale of the map image

  return (
    'https://maps.googleapis.com/maps/api/staticmap?' +
    `markers=${encodedMarkersAddress}&zoom=${zoom}&size=${GOOGLE_MAP_MAX_WIDTH}x${height}&scale=${scale}&key=${googleApiKey}`
  );
};

export function VenueContent({ venueInfo }: VenueContentProps): JSX.Element {
  const { translate } = useTranslate();
  const { map, venueText, venueNameText, addressText, getDirectionButton } = useStyles();
  const {
    name: venueName,
    address: { address1, address2, address3, city, region, country, postalCode }
  } = venueInfo;
  const absoluteAddress = [address1, address2, address3, city, region, country, postalCode]
    .filter(address => !!address)
    .join(', ');
  const encodedMarkersAddress = encodeURIComponent([venueName, absoluteAddress].join(', '));
  const googleLocationRedirectUrl = `https://maps.google.com/?q=${encodedMarkersAddress}`;
  const uri = getGoogleMapUri(encodedMarkersAddress, publicRuntimeConfig.GOOGLE_MAPS_API_KEY);
  return (
    <>
      <h2 css={venueText}>{translate('LandingPage-Venue-Text')}</h2>
      <div css={venueNameText}>{venueName}</div>
      <div css={addressText}>{absoluteAddress}</div>

      <>
        <div css={map}>
          <a target="_blank" href={googleLocationRedirectUrl} rel="noreferrer">
            <img
              aria-label={translate('LandingPage-LocationMap-GoogleMapAriaAltTxt')}
              alt={translate('LandingPage-LocationMap-GoogleMapAriaAltTxt')}
              src={uri}
              width="100%"
              height="100%"
            />
          </a>
        </div>
        <div css={getDirectionButton}>
          <Button
            element="a"
            target="_blank"
            href={googleLocationRedirectUrl}
            rel="noreferrer"
            size="m"
            role="button"
            text={translate('LandingPage-Get-Directions-Btn')}
          />
        </div>
      </>
    </>
  );
}
